import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  type ApplicationConfig,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { provideAppFooter } from '@sbe-spinoff-client/app-footer';
import { provideAppShell, provideShellTitleStrategy, withNoMatchRedirect } from '@sbe-spinoff-client/app-shell';
import {
  NOTIFICATIONS_API_CLIENT,
  provideSchoolbellOrgLinkApiBaseUrl,
  provideSpinoffAuth,
  resolveOrgScopedId,
  spinoffAuthInterceptor,
  spinoffTwoFactorAuthInterceptor,
  type SpinoffAuthMethod,
  type SpinoffOidcProviderButton,
} from '@sbe-spinoff-client/spinoff-platform';
import { appRoutes } from './app.routes';
import { environment } from './environments/environment';
import { NotificationsApiService } from './core/api';
import {
  smsApiBaseUrlInterceptor,
  smsHttpApiBaseUrlProvider,
} from './core/sms-http-api-base';

// Phase 2(2026-08-13) — 자체 로그인(이메일+비밀번호·직접 소셜 버튼) 제거, "학교종이로
// 계속하기" 단일 창구로 전환(commerce PR #1813, timetable PR #1810과 동일 패턴). methods가
// 빈 배열이고 oidcProviders가 하나뿐이면 SpinoffLoginComponent가 method-select 없이 곧장
// 'social' 스텝으로 진입한다 — UI는 이 설정 변경만으로 전환된다. 백엔드(SpinoffBffModule의
// signup/signin/social*/otp* 엔드포인트)는 그대로 둔다 — refresh/org-token/signout/me는
// schoolbell 로그인 경로도 공유한다.
const loginMethods: SpinoffAuthMethod[] = [];

const oidcProviders: SpinoffOidcProviderButton[] = [
  {
    id: 'schoolbell',
    label: '학교종이로 계속하기',
    startUrl: `${environment.smsApiBaseUrl}/auth/sso/schoolbell-e/authorize-redirect`,
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    // zoneless 변경 감지 — OnPush 미적용 컴포넌트도 전부 signal 기반 상태(다이얼로그 close 콜백)이거나 FormControl.setValue()(CD와
    // 무관하게 자체 DOM 동기화 구독)로 끝난다. NICE 본인인증·멤버 피커 팝업의 setInterval+postMessage 플로우도 최종적으로
    // signal.set()으로 귀결된다.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    provideSpinoffAuth({
      apiBaseUrl: environment.smsApiBaseUrl,
      methods: loginMethods,
      oidcProviders,
      loginPath: '/login',
      resolveOrgScopedId,
    }),
    smsHttpApiBaseUrlProvider,
    provideSchoolbellOrgLinkApiBaseUrl(() => environment.smsApiBaseUrl),
    provideHttpClient(withXhr(), 
      withInterceptors([
        smsApiBaseUrlInterceptor,
        // 발송 API의 428(투팩터 인증 필요) → 인증 다이얼로그 → 같은 요청 재전송. 재전송이 인증 헤더 부착부터 다시 지나도록 spinoffAuthInterceptor 앞에 둔다.
        spinoffTwoFactorAuthInterceptor,
        spinoffAuthInterceptor,
      ]),
    ),
    // paramsInheritanceStrategy:'always' — /orgs/:orgId 하위 비어있지 않은 경로
    // (history/send 등) 자식 컴포넌트도 조상 라우트의 orgId param 을 input() 으로
    // 자동 바인딩받게 한다. 기본 'emptyOnly' 는 빈 경로 자식에게만 상속.
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withNoMatchRedirect(),
    ),
    provideAppFooter({ statusAppSlug: 'sbe-sms' }),
    provideAppShell({ brandText: '학교종이 문자', showThemeToggle: true }),
    provideShellTitleStrategy(),
    { provide: NOTIFICATIONS_API_CLIENT, useExisting: NotificationsApiService },
  ],
};
